/**
 * runtimeFormService — talks to the Runtime Submission Platform API.
 *
 *   POST /runtime/forms/resolve      → resolve form + resume-draft
 *   POST /runtime/forms/save-draft   → persist a DynamicFormSubmission draft
 *   POST /runtime/forms/submit       → finalize + dispatch to entity service
 *
 * The legacy POST /runtime/submit dispatcher has been removed. The `submit`
 * wrapper below preserves the existing RuntimeFormPage call shape by
 * translating (context, mode) → (EntityType, ActionType) for the new
 * endpoint. New callers should prefer `resolve` / `saveDraft` / `submitForm`
 * directly.
 */

import apiClient from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type { FieldValue } from '../components/workspace/dynamicFormFieldUtils';
import type { RuntimeContextKey, RuntimeMode } from '../runtime/contextMap';

// ── Backend EntityType / ActionType enums (string literals) ──────────────
export type BackendEntityType =
  | 'Hospital'
  | 'HCP'
  | 'Patient'
  | 'Pharmacy'
  | 'DispenseCase'
  | 'Case'
  | 'CallLog';

export type BackendActionType =
  | 'CREATE'
  | 'EDIT'
  | 'VIEW'
  | 'DISPENSE_REQUEST'
  | 'DISPENSE_APPROVE'
  | 'DISPENSE_REJECT'
  | 'DISPENSE_COMPLETE';

// ── Resolve ──────────────────────────────────────────────────────────────
export interface RuntimeFormResolveRequest {
  programId: string;
  entityType: BackendEntityType;
  actionType: BackendActionType;
  workflowStepId?: string | null;
  entityId?: string | null;
}

export interface RuntimeFormSubmissionSnapshot {
  dynamicFormSubmissionId: string;
  programId: string;
  programFormId: string;
  formVersion: number;
  entityType: string;
  entityId?: string | null;
  parentEntityType?: string | null;
  parentEntityId?: string | null;
  workflowStepId?: string | null;
  actionType: string;
  submissionStatus: string;
  draftDataJson?: string | null;
  submittedDataJson?: string | null;
  createdByUserId: string;
  ownedByUserId?: string | null;
  lastEditedByUserId?: string | null;
  submittedByUserId?: string | null;
  submittedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  correlationId: string;
}

export interface RuntimeFormResolveResponse {
  programId: string;
  entityType: string;
  actionType: string;
  workflowStepId?: string | null;
  entityId?: string | null;
  programFormId: string;
  formVersion: number;
  formName?: string | null;
  formSchemaJson: string;
  isRegistration: boolean;
  enableLoginCreation: boolean;
  targetRoleId?: string | null;
  resumeDraftSubmission?: RuntimeFormSubmissionSnapshot | null;
}

// ── Draft / Submit ───────────────────────────────────────────────────────
export interface RuntimeFormSaveDraftRequest {
  programId: string;
  entityType: BackendEntityType;
  actionType: BackendActionType;
  workflowStepId?: string | null;
  entityId?: string | null;
  parentEntityId?: string | null;
  parentEntityType?: string | null;
  programFormId: string;
  formVersion: number;
  dynamicFormSubmissionId?: string | null;
  formData: Record<string, FieldValue>;
  changeSummary?: string | null;
}

export interface RuntimeFormSubmitRequestBackend {
  programId: string;
  entityType: BackendEntityType;
  actionType: BackendActionType;
  workflowStepId?: string | null;
  entityId?: string | null;
  parentEntityId?: string | null;
  parentEntityType?: string | null;
  programFormId: string;
  formVersion: number;
  dynamicFormSubmissionId?: string | null;
  formData: Record<string, FieldValue>;
  parentIds?: Record<string, string>;
  changeSummary?: string | null;
}

export interface RuntimeFormSubmitResult {
  dynamicFormSubmissionId: string;
  entityId: string;
  entityType: string;
  actionType: string;
  submissionStatus: string;
  workflowStepId?: string | null;
  createdUserId?: string | null;
}

// ── Legacy-shape wrapper (back-compat for RuntimeFormPage) ───────────────
export interface RuntimeSubmitRequest {
  programId: string;
  context: RuntimeContextKey;
  mode: RuntimeMode;
  entityId?: string | null;
  formId: string;
  formVersion: number;
  formData: Record<string, FieldValue>;
  parentIds?: Record<string, string>;
}

export interface RuntimeSubmitResult {
  entityId: string;
  context: RuntimeContextKey;
  mode: RuntimeMode;
  status?: string;
  workflowStepId?: string | null;
}

const CONTEXT_TO_ENTITY_TYPE: Record<RuntimeContextKey, BackendEntityType> = {
  hospital: 'Hospital',
  hcp: 'HCP',
  patient: 'Patient',
  scp: 'Case',
  callLog: 'CallLog',
  pharmacy: 'Pharmacy',
  dispenseCase: 'DispenseCase',
};

function modeToActionType(mode: RuntimeMode): BackendActionType {
  return mode === 'edit' ? 'EDIT' : 'CREATE';
}

// ── Service ──────────────────────────────────────────────────────────────
export const runtimeFormService = {
  async resolve(
    request: RuntimeFormResolveRequest,
  ): Promise<ApiResponse<RuntimeFormResolveResponse>> {
    const response = await apiClient.post<ApiResponse<RuntimeFormResolveResponse>>(
      '/runtime/forms/resolve',
      request,
    );
    return response.data;
  },

  async saveDraft(
    request: RuntimeFormSaveDraftRequest,
  ): Promise<ApiResponse<RuntimeFormSubmissionSnapshot>> {
    const response = await apiClient.post<ApiResponse<RuntimeFormSubmissionSnapshot>>(
      '/runtime/forms/save-draft',
      request,
    );
    return response.data;
  },

  async submitForm(
    request: RuntimeFormSubmitRequestBackend,
  ): Promise<ApiResponse<RuntimeFormSubmitResult>> {
    const response = await apiClient.post<ApiResponse<RuntimeFormSubmitResult>>(
      '/runtime/forms/submit',
      request,
    );
    return response.data;
  },

  /**
   * Legacy wrapper kept so existing RuntimeFormPage callers keep compiling.
   * Translates the (context, mode) tuple into the new (entityType, actionType)
   * contract and re-maps the backend result back to the old shape.
   */
  async submit(request: RuntimeSubmitRequest): Promise<ApiResponse<RuntimeSubmitResult>> {
    const entityType = CONTEXT_TO_ENTITY_TYPE[request.context];
    const actionType = modeToActionType(request.mode);
    const backendRequest: RuntimeFormSubmitRequestBackend = {
      programId: request.programId,
      entityType,
      actionType,
      entityId: request.entityId ?? null,
      programFormId: request.formId,
      formVersion: request.formVersion,
      formData: request.formData,
      parentIds: request.parentIds,
    };

    const raw = await runtimeFormService.submitForm(backendRequest);
    if (!raw.success || !raw.data) {
      return {
        success: raw.success,
        message: raw.message,
        errors: raw.errors,
        correlationId: raw.correlationId,
        data: undefined as unknown as RuntimeSubmitResult,
      };
    }
    const mapped: RuntimeSubmitResult = {
      entityId: raw.data.entityId,
      context: request.context,
      mode: request.mode,
      status: raw.data.submissionStatus,
      workflowStepId: raw.data.workflowStepId ?? null,
    };
    return {
      success: true,
      message: raw.message,
      errors: raw.errors,
      correlationId: raw.correlationId,
      data: mapped,
    };
  },
};

